import { useIntl } from 'react-intl';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { classNames } from 'primereact/utils';
import InfoTip from '../../components/novarium/InfoTip';
import { WaitRankingRowDTO } from '../../dto/ServiceStatisticsDTO';
import { LEVEL_LABEL_ID, LEVEL_TAG_CLASS, RANKING_LEVEL } from './waitLevelText';
import { isCriticalPriorityCause, MAX_CAUSES_SHOWN } from './waitCauseText';
import WaitCauseList from './WaitCauseList';
import { modeMessageId, StatisticsMode } from './statisticsMode';
import { formatMinutes } from './statisticsUtils';

/** Cómo está el ranking: se está calculando (todavía sin filas), falló, o ya está. */
export type WaitRankingStatus = 'loading' | 'failed' | 'ready';

interface WaitRankingProps {
    /** Las filas que se muestran, ya acotadas a la agenda elegida y recortadas a las primeras cinco. */
    rows: WaitRankingRowDTO[];
    status: WaitRankingStatus;
    /** Los datos que se ven no son los de los últimos filtros: se están recalculando. */
    refreshing: boolean;
    /** Servicio o agenda aplicado como filtro; 0 si no hay. Con uno aplicado, las filas no repiten su nombre (está en el chip). */
    selectedServiceId: number;
    /** Cantidad de avisos del período; con más de los que se ven hay enlace a la lista completa. */
    noticeCount: number;
    /** Tocar la fila: aplica el servicio o la agenda como filtro de la pantalla. */
    onViewService: (serviceId: number) => void;
    /** "Ver los N avisos": abre la lista completa. */
    onOpenAlerts: () => void;
    /** «Reintentar» cuando el ranking no se pudo calcular. */
    onRetry: () => void;
    mode?: StatisticsMode;
}

/**
 * «Dónde hay más espera»: hasta cinco agendas y servicios, de mayor a menor espera media, cada uno con su semáforo (con
 * texto) y sus posibles causas (hasta tres, una por renglón). Toda la fila es clickeable (con una flecha al final) y filtra
 * la pantalla por esa agenda o servicio, también con el teclado. Debajo, el enlace a
 * la lista completa de avisos. El backend calcula el ranking, el nivel y las causas: acá solo se muestran.
 * Con una agenda o servicio ya elegido en los filtros el bloque explica esa espera sin repetir el nombre.
 * Si el ranking no se pudo calcular, un texto con «Reintentar»: el resto de la pantalla sigue funcionando.
 * TODO(design-system): el ranking no está en Novarium; usa filas como la lista de avisos y la cabecera de una card. El
 * resalte de la fila con espera larga en Inmediato o Emergencia (barra a la izquierda, ícono y negrita) tampoco está definido.
 */
export default function WaitRanking({
    rows, status, refreshing, selectedServiceId, noticeCount, onViewService, onOpenAlerts, onRetry, mode = 'management',
}: WaitRankingProps) {
    const intl = useIntl();
    const selected = selectedServiceId !== 0;

    return (
        <section className="nv-card ss-ranking" aria-labelledby="ss-ranking-title" aria-busy={status === 'loading' || refreshing}>
            <div className="ss-ranking__head">
                <h2 className="nv-section-title" id="ss-ranking-title">
                    {intl.formatMessage({ id: selected ? 'ServicesStatisticsRankingTitleSelected' : modeMessageId(mode, 'RankingTitle') })}
                </h2>
                <InfoTip text={intl.formatMessage({ id: 'ServicesStatisticsCausesNote' })} />
            </div>
            {status === 'loading' && (
                <p className="nv-note" role="status">
                    <i className="pi pi-info-circle" aria-hidden="true" />
                    {intl.formatMessage({ id: 'ServicesStatisticsRankingLoading' })}
                </p>
            )}
            {status === 'failed' && (
                <div className="ss-ranking__failed" role="status">
                    <p className="nv-note">
                        <i className="pi pi-info-circle" aria-hidden="true" />
                        {intl.formatMessage({ id: 'ServicesStatisticsRankingFailed' })}
                    </p>
                    <Button type="button" link label={intl.formatMessage({ id: 'ServicesStatisticsRetry' })} onClick={onRetry} />
                </div>
            )}
            {status === 'ready' && rows.length === 0 && (
                <p className="nv-note">
                    <i className="pi pi-info-circle" aria-hidden="true" />
                    {intl.formatMessage({ id: 'ServicesStatisticsRankingEmpty' })}
                </p>
            )}
            {status === 'ready' && rows.length > 0 && (
                <ul className={classNames('ss-ranking__list', { 'ss-blurred': refreshing })}>
                    {rows.map(row => {
                        const level = RANKING_LEVEL[row.level];
                        const priority = row.causes.slice(0, MAX_CAUSES_SHOWN).some(isCriticalPriorityCause);
                        const rowClass = classNames('ss-ranking__row', {
                            'ss-ranking__row--selected': selected, 'ss-ranking__row--priority': priority,
                        });
                        const content = (
                            <>
                                {!selected && <span className="ss-ranking__name" title={row.serviceName}>{row.serviceName}</span>}
                                <span className="ss-ranking__wait">
                                    <span className="ss-ranking__minutes">{formatMinutes(row.avgWaitMinutes)}</span>
                                    <Tag className={LEVEL_TAG_CLASS[level]} value={intl.formatMessage({ id: LEVEL_LABEL_ID[level] })} />
                                </span>
                                <WaitCauseList causes={row.causes} level={row.level} />
                            </>
                        );
                        // Con una agenda ya elegida la fila solo explica su espera: no filtra nada. Si no, toda la fila es un
                        // único botón (Enter o Espacio, foco visible) que aplica la agenda o el servicio como filtro.
                        return (
                            <li key={row.serviceId} className="ss-ranking__item">
                                {selected ? (
                                    <div className={rowClass}>{content}</div>
                                ) : (
                                    <div className={classNames(rowClass, 'ss-ranking__row--action')} role="button" tabIndex={0}
                                        aria-label={intl.formatMessage({ id: 'ServicesStatisticsRankingRowLabel' }, { name: row.serviceName })}
                                        onClick={() => onViewService(row.serviceId)}
                                        onKeyDown={event => {
                                            // Lo que tenga adentro (una etiqueta con tooltip) maneja su propio teclado
                                            if (event.target !== event.currentTarget) return;
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                onViewService(row.serviceId);
                                            }
                                        }}>
                                        {content}
                                        <i className="pi pi-chevron-right ss-ranking__chevron" aria-hidden="true" />
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
            {status === 'ready' && noticeCount > rows.length && (
                <div className="ss-ranking__more">
                    <Button type="button" link
                        label={intl.formatMessage({ id: 'ServicesStatisticsViewAlerts' }, { count: noticeCount })}
                        onClick={onOpenAlerts} />
                </div>
            )}
        </section>
    );
}
